import React, { useState, useMemo } from 'react';
import {
  ClipboardDocumentListIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  BookmarkIcon,
  ExclamationTriangleIcon,
  BellIcon,
  ClockIcon,
  ArchiveBoxIcon,
  TagIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { BookmarkIcon as BookmarkSolidIcon } from '@heroicons/react/24/solid';
import { useNotes } from '../hooks/useNotes';
import {
  Note,
  NoteCategory,
  NotePriority,
  formatRelativeTime,
  getPriorityColor,
  getCategoryColor,
} from '../api/notes';
import CreateNoteModal from '../components/notes/CreateNoteModal';
import EditNoteModal from '../components/notes/EditNoteModal';
import ViewNoteModal from '../components/notes/ViewNoteModal';
import DeleteConfirmModal from '../components/common/DeleteConfirmModal';
import PageHero from '../components/ui/PageHero';
import EmptyState from '../components/ui/EmptyState';

const Notes: React.FC = () => {
  const {
    notes,
    statistics,
    loading,
    error,
    createNote,
    updateNote,
    deleteNote,
    togglePin,
    toggleArchive,
    refreshData,
  } = useNotes();

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedNote, setSelectedNote] = useState<Note | null>(null);

  // Filter and search states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<NoteCategory | 'all'>('all');
  const [selectedPriority, setSelectedPriority] = useState<NotePriority | 'all'>('all');
  const [showPinnedOnly, setShowPinnedOnly] = useState(false);
  const [showArchivedOnly, setShowArchivedOnly] = useState(false);
  const [showRemindersOnly, setShowRemindersOnly] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Sort state
  const [sortBy, setSortBy] = useState<'createdAt' | 'updatedAt' | 'title' | 'priority'>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Filtered and sorted notes
  const filteredNotes = useMemo(() => {
    let filtered = notes.filter(note => {
      const matchesSearch = !searchTerm || 
        note.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        note.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
        note.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesCategory = selectedCategory === 'all' || note.category === selectedCategory;
      const matchesPriority = selectedPriority === 'all' || note.priority === selectedPriority;
      const matchesPinned = !showPinnedOnly || note.isPinned;
      const matchesArchived = showArchivedOnly ? note.isArchived : !note.isArchived;
      const matchesReminders = !showRemindersOnly || !!note.reminderDate;
      
      return matchesSearch && matchesCategory && matchesPriority && matchesPinned && matchesArchived && matchesReminders;
    });

    // Apply sorting
    filtered.sort((a, b) => {
      // Always show pinned notes first when sorting by createdAt desc
      if (sortBy === 'createdAt' && sortOrder === 'desc') {
        if (a.isPinned !== b.isPinned) {
          return a.isPinned ? -1 : 1;
        }
      }

      let aValue: any = a[sortBy];
      let bValue: any = b[sortBy];

      if (sortBy === 'createdAt' || sortBy === 'updatedAt') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      } else if (sortBy === 'priority') {
        const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1 };
        aValue = priorityOrder[a.priority];
        bValue = priorityOrder[b.priority];
      } else if (typeof aValue === 'string') {
        aValue = aValue?.toLowerCase() || '';
        bValue = bValue?.toLowerCase() || '';
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [notes, searchTerm, selectedCategory, selectedPriority, showPinnedOnly, showArchivedOnly, showRemindersOnly, sortBy, sortOrder]);

  const handleCreateNote = async (data: any) => {
    const result = await createNote(data);
    if (result) {
      setShowCreateModal(false);
    }
  };

  const handleUpdateNote = async (data: any) => {
    if (selectedNote) {
      const result = await updateNote(selectedNote.id, data);
      if (result) {
        setShowEditModal(false);
        setSelectedNote(null);
      }
    }
  };

  const handleDeleteNote = async () => {
    if (selectedNote) {
      const result = await deleteNote(selectedNote.id);
      if (result) {
        setShowDeleteModal(false);
        setSelectedNote(null);
      }
    }
  };

  const handleTogglePin = async (note: Note) => {
    await togglePin(note.id);
  };

  const handleToggleArchive = async (note: Note) => {
    await toggleArchive(note.id);
  };

  const openEditModal = (note: Note) => {
    setSelectedNote(note);
    setShowEditModal(true);
  };

  const openViewModal = (note: Note) => {
    setSelectedNote(note);
    setShowViewModal(true);
  };

  const openDeleteModal = (note: Note) => {
    setSelectedNote(note);
    setShowDeleteModal(true);
  };

  if (loading && notes.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error loading notes</h3>
            <div className="mt-2 text-sm text-red-700">
              <p>{error}</p>
            </div>
            <div className="mt-4">
              <button
                onClick={refreshData}
                className="bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <PageHero
        title="Notes"
        subtitle="Observations, reminders, and important notes about your birds"
        actions={
          <button type="button" onClick={() => setShowCreateModal(true)} className="btn btn-primary focus-pop">
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            Add Note
          </button>
        }
      />

      {/* Statistics Cards */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ClipboardDocumentListIcon className="h-6 w-6 text-slate-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-slate-500 truncate">Total Notes</dt>
                    <dd className="text-lg font-medium text-slate-900">{statistics.total}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <BookmarkIcon className="h-6 w-6 text-blue-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-slate-500 truncate">Pinned</dt>
                    <dd className="text-lg font-medium text-slate-900">{statistics.pinned}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <BellIcon className="h-6 w-6 text-yellow-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-slate-500 truncate">With Reminders</dt>
                    <dd className="text-lg font-medium text-slate-900">{statistics.withReminders}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ClockIcon className="h-6 w-6 text-green-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-slate-500 truncate">Recent (7 days)</dt>
                    <dd className="text-lg font-medium text-slate-900">{statistics.recentNotes}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="flex items-center w-full rounded-md border border-gray-300 bg-white focus-within:border-cyan-500 focus-within:ring-1 focus-within:ring-cyan-500">
              <span className="pl-3 pr-2 flex items-center flex-shrink-0 text-slate-400" aria-hidden="true">
                <MagnifyingGlassIcon className="h-5 w-5" />
              </span>
              <input
                type="text"
                className="flex-1 min-w-0 py-2 pr-3 border-0 bg-transparent leading-5 placeholder-gray-500 focus:outline-none focus:ring-0 focus:placeholder-gray-400 sm:text-sm"
                placeholder="Search notes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-slate-700 bg-white hover:bg-slate-50/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
            >
              <FunnelIcon className="h-4 w-4 mr-2" />
              Filters
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <div>
                <label htmlFor="category-filter" className="block text-sm font-medium text-slate-700">
                  Category
                </label>
                <select
                  id="category-filter"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value as any)}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm rounded-md"
                >
                  <option key="all-1753461297381" value="all">All Categories</option>
                  {Object.values(NoteCategory).map(category => (
                    <option key={category} value={category}>
                      {category.charAt(0).toUpperCase() + category.slice(1).replace('_', ' ')}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="priority-filter" className="block text-sm font-medium text-slate-700">
                  Priority
                </label>
                <select
                  id="priority-filter"
                  value={selectedPriority}
                  onChange={(e) => setSelectedPriority(e.target.value as any)}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm rounded-md"
                >
                  <option key="all-1753461297381" value="all">All Priorities</option>
                  {Object.values(NotePriority).map(priority => (
                    <option key={priority} value={priority}>
                      {priority.charAt(0).toUpperCase() + priority.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="sort-by" className="block text-sm font-medium text-slate-700">
                  Sort By
                </label>
                <select
                  id="sort-by"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm rounded-md"
                >
                  <option key="createdAt-1753461297381" value="createdAt">Date Created</option>
                  <option key="updatedAt-1753461297381" value="updatedAt">Date Updated</option>
                  <option key="title-1753461297381" value="title">Title</option>
                  <option key="priority-1753461297381" value="priority">Priority</option>
                </select>
              </div>

              <div>
                <label htmlFor="sort-order" className="block text-sm font-medium text-slate-700">
                  Sort Order
                </label>
                <select
                  id="sort-order"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm rounded-md"
                >
                  <option key="desc-1753461297381" value="desc">Newest First</option>
                  <option key="asc-1753461297381" value="asc">Oldest First</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">Show</label>
                <div className="space-y-1">
                  <label className="inline-flex items-center">
                    <input
                      type="checkbox"
                      checked={showPinnedOnly}
                      onChange={(e) => setShowPinnedOnly(e.target.checked)}
                      className="rounded border-gray-300 text-cyan-600 shadow-sm focus:border-cyan-300 focus:ring focus:ring-cyan-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-slate-700">Pinned only</span>
                  </label>
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">View</label>
                <div className="space-y-1">
                  <label className="inline-flex items-center">
                    <input
                      type="checkbox"
                      checked={showArchivedOnly}
                      onChange={(e) => setShowArchivedOnly(e.target.checked)}
                      className="rounded border-gray-300 text-cyan-600 shadow-sm focus:border-cyan-300 focus:ring focus:ring-cyan-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-slate-700">Archived</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="checkbox"
                      checked={showRemindersOnly}
                      onChange={(e) => setShowRemindersOnly(e.target.checked)}
                      className="rounded border-gray-300 text-cyan-600 shadow-sm focus:border-cyan-300 focus:ring focus:ring-cyan-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-slate-700">With reminders</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Notes List */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-slate-900">
            Notes ({filteredNotes.length})
          </h3>
        </div>

        {filteredNotes.length === 0 ? (
          <EmptyState
            className="mt-0 rounded-none border-0 shadow-none bg-transparent"
            title="No notes found"
            description={
              notes.length === 0
                ? 'Get started by creating your first note.'
                : 'Try adjusting your search criteria.'
            }
            icon={<ClipboardDocumentListIcon className="h-6 w-6" />}
            action={
              notes.length === 0 ? (
                <button type="button" onClick={() => setShowCreateModal(true)} className="btn btn-primary text-sm focus-pop">
                  <PlusIcon className="h-4 w-4" />
                  Add Note
                </button>
              ) : undefined
            }
          />
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredNotes.map((note) => (
              <div
                key={note.id}
                className="p-6 hover:bg-slate-50/80 cursor-pointer"
                onClick={() => openViewModal(note)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-2">
                      <h4 className="text-lg font-medium text-slate-900 truncate">
                        {note.title}
                      </h4>
                      {note.isPinned && (
                        <BookmarkSolidIcon className="h-4 w-4 text-blue-500" />
                      )}
                      {note.isArchived && (
                        <ArchiveBoxIcon className="h-4 w-4 text-slate-500" />
                      )}
                      {note.reminderDate && (
                        <BellIcon className="h-4 w-4 text-yellow-500" />
                      )}
                    </div>
                    
                    <div className="flex items-center space-x-4 mb-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(note.category)}`}>
                        {note.category.charAt(0).toUpperCase() + note.category.slice(1)}
                      </span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(note.priority)}`}>
                        {note.priority.charAt(0).toUpperCase() + note.priority.slice(1)}
                      </span>
                      <span className="text-sm text-slate-500">
                        {formatRelativeTime(note.createdAt)}
                      </span>
                    </div>

                    <p className="text-sm text-slate-600 line-clamp-2 mb-2">
                      {note.content}
                    </p>

                    {note.tags && note.tags.length > 0 && (
                      <div className="flex items-center">
                        <TagIcon className="h-4 w-4 text-slate-400 mr-1" />
                        <div className="flex flex-wrap gap-1">
                          {note.tags.slice(0, 3).map((tag, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 text-xs bg-gray-100 text-slate-600 rounded"
                            >
                              {tag}
                            </span>
                          ))}
                          {note.tags.length > 3 && (
                            <span className="text-xs text-slate-500">
                              +{note.tags.length - 3} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex-shrink-0 ml-4">
                    <div className="flex space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTogglePin(note);
                        }}
                        className={`p-2 rounded-full hover:bg-gray-100 ${note.isPinned ? 'text-cyan-600' : 'text-slate-400'}`}
                        title={note.isPinned ? 'Unpin' : 'Pin'}
                      >
                        <BookmarkIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditModal(note);
                        }}
                        className="p-2 text-slate-400 hover:text-slate-600 hover:bg-gray-100 rounded-full"
                        title="Edit"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleArchive(note);
                        }}
                        className={`p-2 rounded-full hover:bg-gray-100 ${note.isArchived ? 'text-yellow-600' : 'text-slate-400'}`}
                        title={note.isArchived ? 'Unarchive' : 'Archive'}
                      >
                        <ArchiveBoxIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openDeleteModal(note);
                        }}
                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-gray-100 rounded-full"
                        title="Delete"
                      >
                        <TrashIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      <CreateNoteModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateNote}
      />

      <EditNoteModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedNote(null);
        }}
        onSubmit={handleUpdateNote}
        note={selectedNote}
      />

      <ViewNoteModal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setSelectedNote(null);
        }}
        note={selectedNote}
        onEdit={() => {
          setShowViewModal(false);
          setShowEditModal(true);
        }}
        onDelete={() => {
          setShowViewModal(false);
          setShowDeleteModal(true);
        }}
        onTogglePin={handleTogglePin}
        onToggleArchive={handleToggleArchive}
      />

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedNote(null);
        }}
        onConfirm={handleDeleteNote}
        title="Delete Note"
        message={`Are you sure you want to delete the note "${selectedNote?.title}"? This action cannot be undone.`}
      />
    </div>
  );
};

export default Notes; 